import React, { useEffect, useState } from "react";
import axios from "axios";

const DailyLoanToPayPage = ({ selectedDate }) => {
  const [dailyLoans, setDailyLoans] = useState([]);
  const [logPayments, setLogPayments] = useState([]);
  const [paidAmounts, setPaidAmounts] = useState({});
  const [loading, setLoading] = useState(true);
  const baseURL = process.env.REACT_APP_BASE_URL;

  const formatDate = (dateString) => {
    try {
      if (!dateString) {
        console.error("No date string provided");
        return null;
      }

      const [day, month, year] = dateString.split("-");
      if (!day || !month || !year) {
        console.error(`Invalid date format encountered: ${dateString}`);
        return null;
      }

      const formattedDate = `${year}-${month}-${day}`;
      const date = new Date(formattedDate);

      if (isNaN(date)) {
        console.error(`Invalid date after formatting: ${formattedDate}`);
        return null;
      }

      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error(`Error formatting date: ${dateString}`, error);
      return null;
    }
  };

  const calculateDaysFromEndDate = (endDate) => {
    const end = new Date(endDate);
    const currentDate = new Date();
    const differenceInMs = end - currentDate;
    const pendingDays = Math.ceil(differenceInMs / (1000 * 60 * 60 * 24));
    return pendingDays >= 0 ? pendingDays : 0;
  };

  useEffect(() => {
    const fetchDailyLoans = async () => {
      try {
        const accessToken = sessionStorage.getItem("access_token");
        const response = await axios.get(`${baseURL}/getAllLoan`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const filteredData = response.data.filter(
          (item) => item.scheme_name?.toLowerCase() === "daily collection"
        );
        setDailyLoans(filteredData);

        const logResponse = await axios.get(`${baseURL}/fetchData`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setLogPayments(logResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedDate) {
      fetchDailyLoans();
    }
  }, [selectedDate, baseURL]);

  useEffect(() => {
    if (dailyLoans.length && logPayments.length && selectedDate) {
      const amounts = {};
      const logsByLoanId = {};
      const parsedSelectedDate = new Date(formatDate(selectedDate));

      logPayments.forEach((log) => {
        if (!logsByLoanId[log.loan_id]) {
          logsByLoanId[log.loan_id] = [];
        }
        logsByLoanId[log.loan_id].push(log);
      });

      dailyLoans.forEach((loan) => {
        const logs = logsByLoanId[loan.loan_id] || [];
        const paidAmount = logs
          .filter((log) => {
            const logDate = new Date(formatDate(log.date));
            return logDate <= parsedSelectedDate;
          })
          .reduce((total, log) => total + (parseFloat(log.emi_amount) || 0), 0);

        amounts[loan.loan_id] = paidAmount;
      });

      setPaidAmounts(amounts);
    }
  }, [dailyLoans, logPayments, selectedDate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-lg font-semibold text-gray-600">Loading…</p>
      </div>
    );
  }

  return (
    <div className="p-5 bg-gray-50">
      <h2 className="text-lg font-bold mb-5 text-center">
        Daily Loan Payments
      </h2>
      <h3 className="text-lg font-bold mb-3 text-center">
        Selected Date: {selectedDate}
      </h3>

      {dailyLoans.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="table-auto w-full border-collapse border border-gray-300 bg-white shadow-md rounded-lg">
            <thead className="bg-gray-200">
              <tr>
                <th className="border border-gray-300 px-4 py-2 w-20 text-left">
                  S.No
                </th>
                <th className="border border-gray-300 px-4 py-2 w-30 text-left">
                  Customer Name
                </th>
                <th className="border border-gray-300 px-4 py-2 w-30 text-left">
                  Days
                </th>
                <th className="border border-gray-300 px-4 py-2 w-30 text-left">
                  Amount
                </th>
                <th className="border border-gray-300 px-4 py-2 w-30 text-left">
                  Agent
                </th>
              </tr>
            </thead>
            <tbody>
              {dailyLoans.map((item, idx) => {
                const paidAmount = paidAmounts[item.loan_id] || 0;
                const remainingAmount = item.amount - paidAmount;

                return (
                  <tr
                    key={item.id}
                    className={`${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-100"
                    } border-b border-gray-200`}
                  >
                    <td className="border border-gray-300 px-4 py-2 w-20">
                      {idx + 1}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 w-30">
                      {item.tamilname || "N/A"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 w-30">
                      {calculateDaysFromEndDate(item.end_date)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 w-30">
                      {remainingAmount}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 w-30">
                      {item.collectionagent_name || "N/A"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          No data available for the selected date.
        </div>
      )}
    </div>
  );
};

export default DailyLoanToPayPage;
